import ObjectCreationValidator from '@/entities/validators/ObjectCreationValidator'
import { ProductDomainItemI } from '@/modules/myServices/types/api/Service'
import { DomainManagementAdditionalField } from '@/modules/domainManagement/types/api/DomainManagementAdditionalFieldsResponse'
import { DomainRegistrationError } from '@/modules/domainManagement/types/api/DomainManagementRegistrationError'
import { formatDate } from '@/utils'

export type DomainStatusI =
  | 'Pending'
  | 'Pending Registration'
  | 'Pending Transfer'
  | 'Active'
  | 'Grace Period (Expired)'
  | 'Redemption Period (Expired)'
  | 'Expired'
  | 'Transferred Away'
  | 'Cancelled'
  | 'Fraud'
  | 'Redemption'
  | 'Grace'
  | 'Terminated'

export type sslI = {
  id: number | null
  user_id: number | null
  domain_name: string | null
  subject_name: string | null
  subject_org: string | null
  issuer_name: string | null
  issuer_org: string | null
  start_date: string | null
  expiry_date: null
  active: 1 | 0 | null
  last_synced_date: string | null
  created_at: string | null
  updated_at: string | null
}

export type AlertI = {
  button: string
  description: string
  title: string
  type: 'success' | 'info' | 'warning' | 'error'
}

export default class DomainItem {
  dnsmanagement: number
  domainname: string
  donotrenew: boolean
  emailforwarding: number
  expirydate: string
  firstpaymentamount: string
  id: number
  idprotection: number
  nextduedate: string
  notes: string
  orderid: number
  paymentmethod: string
  paymentmethodname: string
  promoid: number
  recurringamount: string
  regdate: string
  registrar: string
  regperiod: number
  regtype: string
  status: DomainStatusI
  subscriptionid: string
  userid: number
  ssl: null | SSLStatus
  alerts: AlertI[] | []
  expiringInDays: string
  dateWithoutFormatting: string
  hasDefaultNameservers: boolean
  additionalFields: DomainManagementAdditionalField[]
  registrationError: DomainRegistrationError | null
  ownerChangeFields: string[]
  ownerChangeLocksContacts: boolean

  constructor(product: ProductDomainItemI) {
    this.id = product.id
    this.domainname = product.domainname ?? '-'
    this.dnsmanagement = product.dnsmanagement
    this.donotrenew = !!product.donotrenew
    this.emailforwarding = product.emailforwarding
    this.expirydate = formatDate(product.expirydate)
    this.firstpaymentamount = product.firstpaymentamount
    this.idprotection = product.idprotection
    this.nextduedate = formatDate(product.nextduedate)
    this.notes = product.notes
    this.orderid = product.orderid
    this.paymentmethod = product.paymentmethod
    this.paymentmethodname = product.paymentmethodname
    this.promoid = product.promoid
    this.recurringamount = product.recurringamount
    this.regdate = formatDate(product.regdate)
    this.dateWithoutFormatting = product.regdate
    this.registrar = product.registrar
    this.regperiod = product.regperiod
    this.regtype = product.regtype
    this.status = product.status
    this.subscriptionid = product.subscriptionid
    this.userid = product.userid
    this.ssl = product.ssl ? new SSLStatus(product.ssl) : null
    this.alerts = product.alerts ? product.alerts : []
    this.expiringInDays = this.expiringInDaysString(product.expirydate)
    this.hasDefaultNameservers = product.hasDefaultNameservers ?? true
    this.additionalFields = product.additionalFields ?? []
    // The domain list carries no such key, only the single-domain payload does.
    this.registrationError = product.registrationError ?? null
    // The registrant fields whose change waits until the current and the new owner confirm it; [] in the domain list.
    this.ownerChangeFields = product.ownerChangeFields ?? []
    // Whether the other contacts cannot be changed while such a change waits; false in the domain list.
    this.ownerChangeLocksContacts = product.ownerChangeLocksContacts ?? false

    ObjectCreationValidator.validate(this)
  }

  get detailsButtonDisableStatus() {
    return this.status === 'Terminated' || this.status === 'Cancelled'
  }

  get getChipsColor() {
    switch (this.status) {
      case 'Pending':
        return 'orange'
      case 'Pending Registration':
        return 'orange'
      case 'Pending Transfer':
        return 'orange'
      case 'Active':
        return 'green'
      case 'Grace':
        return 'pink'
      case 'Redemption':
        return 'pink'
      case 'Expired':
        return 'pink'
      case 'Transferred Away':
        return 'dark_orange'
      case 'Cancelled':
        return 'dark_blue'
      case 'Fraud':
        return 'red'
      default:
        return 'gray'
    }
  }

  get getStatusTextColor() {
    switch (this.status) {
      case 'Pending':
        return 'main-light-orange-for-status'
      case 'Pending Registration':
        return 'main-light-orange-for-status'
      case 'Pending Transfer':
        return 'main-light-orange-for-status'
      case 'Active':
        return 'main-dark-green-text'
      case 'Grace':
        return 'main-brightly-pink'
      case 'Redemption':
        return 'main-brightly-pink'
      case 'Expired':
        return 'main-brightly-pink'
      case 'Transferred Away':
        return 'main-orange-text'
      case 'Cancelled':
        return 'main-violet'
      case 'Fraud':
        return 'main-red-text'
      default:
        return 'gray'
    }
  }

  get getRenewStatus() {
    return this.donotrenew ? 'Disabled' : 'Enabled'
  }

  expiringInDaysString(date: string): string {
    const expiryDate = new Date(date)
    const today = new Date()
    const timeDifference = expiryDate.getTime() - today.getTime()
    const daysLeft = Math.ceil(timeDifference / (1000 * 3600 * 24))

    return `Expiring in ${daysLeft} days`
  }
}

class SSLStatus {
  id: number | null
  user_id: number | null
  domain_name: string | null
  subject_name: string | null
  subject_org: string | null
  issuer_name: string | null
  issuer_org: string | null
  start_date: string | null
  expiry_date: null
  active: 1 | 0 | null
  last_synced_date: string | null
  created_at: string | null
  updated_at: string | null

  constructor(ssl: sslI) {
    this.id = ssl.id ? ssl.id : null
    this.user_id = ssl.user_id ? ssl.user_id : null
    this.domain_name = ssl.domain_name ? ssl.domain_name : null
    this.subject_name = ssl.subject_name ? ssl.subject_name : null
    this.subject_org = ssl.subject_org ? ssl.subject_org : null
    this.issuer_name = ssl.issuer_name ? ssl.issuer_name : null
    this.issuer_org = ssl.issuer_org ? ssl.issuer_org : null
    this.start_date = ssl.start_date ? ssl.start_date : null
    this.expiry_date = ssl.expiry_date ? ssl.expiry_date : null
    this.active = ssl.active
    this.last_synced_date = ssl.last_synced_date ? ssl.last_synced_date : null
    this.created_at = ssl.created_at ? ssl.created_at : null
    this.updated_at = ssl.updated_at ? ssl.updated_at : null

    ObjectCreationValidator.validate(this)
  }
}
